import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Spinner from '../_common/Spinner';
import {formatDateWithTime} from '../../helpers/helpers';
import {
    clearWeatherStationStatusReport,
    deleteWeatherStationStatusReport,
    getStationsForStatusReport,
    getWeatherStationStatusReport,
    getWeatherStationStatusReports,
    requestWeatherStationStatusReport
} from '../../store/actions/weatherStationStatusReportsActions';
import './StationStatus.scss';

const formatUtcDateWithTime = dateValue => {
    if (!dateValue) {
        return '-';
    }

    const hasTimezoneInfo = /([zZ]|[+-]\d{2}:\d{2})$/.test(dateValue);
    const normalizedDateValue = hasTimezoneInfo ? dateValue : `${dateValue}Z`;
    return formatDateWithTime(normalizedDateValue);
};

const selector = state => {
    const {language, weatherStationStatusReports} = state;
    return {
        t: language.translator,
        pending: weatherStationStatusReports.pending,
        detailsPending: weatherStationStatusReports.detailsPending,
        stationsPending: weatherStationStatusReports.stationsPending,
        reports: weatherStationStatusReports.reports,
        selectedReport: weatherStationStatusReports.selectedReport,
        stationsForReport: weatherStationStatusReports.stationsForReport
    };
};

const fallbackCopyToBuffer = text => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
};

const copyToBuffer = value => {
    if (value === null || value === undefined || value === '') {
        return Promise.resolve(false);
    }

    const text = String(value);

    if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text)
            .then(() => true)
            .catch(() => fallbackCopyToBuffer(text));
    }

    return Promise.resolve(fallbackCopyToBuffer(text));
};

const StationStatus = () => {
    const dispatch = useDispatch();
    const {
        t,
        pending,
        detailsPending,
        stationsPending,
        reports,
        selectedReport,
        stationsForReport
    } = useSelector(selector);

    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [stationModalOpen, setStationModalOpen] = useState(false);
    const [selectedStationIds, setSelectedStationIds] = useState([]);
    const [intervalsModal, setIntervalsModal] = useState(null);
    const [reportToDelete, setReportToDelete] = useState(null);

    useEffect(() => {
        dispatch(getWeatherStationStatusReports());
    }, [dispatch]);

    useEffect(() => {
        if (stationModalOpen) {
            setSelectedStationIds(stationsForReport.map(station => station.id));
        }
    }, [stationModalOpen, stationsForReport]);

    const refreshReports = () => {
        dispatch(getWeatherStationStatusReports());
    };

    const openReport = report => {
        setReportModalOpen(true);
        dispatch(getWeatherStationStatusReport(report.id));
    };

    const closeReport = () => {
        setReportModalOpen(false);
        dispatch(clearWeatherStationStatusReport());
    };

    const openStationSelection = () => {
        setStationModalOpen(true);
        dispatch(getStationsForStatusReport());
    };

    const closeStationSelection = () => {
        setStationModalOpen(false);
        setSelectedStationIds([]);
    };

    const toggleStation = stationId => {
        setSelectedStationIds(current => current.includes(stationId)
            ? current.filter(id => id !== stationId)
            : [...current, stationId]
        );
    };

    const requestReport = () => {
        const stationIds = [...selectedStationIds];
        closeStationSelection();
        dispatch(requestWeatherStationStatusReport(stationIds))
            .catch(() => {});
    };

    const confirmDelete = () => {
        if (!reportToDelete) {
            return;
        }

        dispatch(deleteWeatherStationStatusReport(reportToDelete.id))
            .then(() => setReportToDelete(null))
            .catch(() => {});
    };

    const statusClass = status => {
        switch (status) {
            case 'OK':
                return 'stationStatusBadge stationStatusBadge--green';
            case 'UNSTABLE':
                return 'stationStatusBadge stationStatusBadge--orange';
            case 'INACTIVE':
            case 'NOT_FOUND':
                return 'stationStatusBadge stationStatusBadge--red';
            default:
                return 'stationStatusBadge stationStatusBadge--gray';
        }
    };

    const activityClass = active => active
        ? 'stationStatusBadge stationStatusBadge--green'
        : 'stationStatusBadge stationStatusBadge--red';

    return (
        <div className='StationStatus'>
            <div className='stationStatusPanel'>
                <div className='stationStatusHeader'>
                    <span>{t.gettext('Station status reports')}</span>
                    <div className='stationStatusHeaderActions'>
                        <button
                            type='button'
                            className='stationStatusIconButton'
                            onClick={refreshReports}
                            title={t.gettext('Refresh')}
                            aria-label={t.gettext('Refresh')}
                        >
                            <span className='material-icons'>refresh</span>
                        </button>
                        <button
                            type='button'
                            className='stationStatusButton'
                            onClick={openStationSelection}
                        >
                            {t.gettext('New report')}
                        </button>
                    </div>
                </div>

                {pending
                    ? (
                        <div className='stationStatusSpinner'>
                            <Spinner size={220}/>
                        </div>
                    )
                    : (
                        <div className='stationStatusList'>
                            <div className='stationStatusListHeader'>
                                <div className='stationStatusListCell stationStatusListCell--time'>
                                    {t.gettext('Report time')}
                                </div>
                                <div className='stationStatusListCell stationStatusListCell--status'>
                                    {t.gettext('Status')}
                                </div>
                                <div className='stationStatusListCell stationStatusListCell--count'>
                                    {t.gettext('Number of stations')}
                                </div>
                                <div className='stationStatusListCell stationStatusListCell--actions'>
                                    {t.gettext('Actions')}
                                </div>
                            </div>
                            {reports.length
                                ? reports.map((report, index) => (
                                    <div
                                        className={[
                                            'stationStatusListRow',
                                            report.unfinished ? 'stationStatusListRow--unfinished' : '',
                                            !report.unfinished ? 'stationStatusListRow--clickable' : ''
                                        ].filter(Boolean).join(' ')}
                                        key={report.id}
                                        onClick={() => {
                                            if (!report.unfinished) {
                                                openReport(report);
                                            }
                                        }}
                                    >
                                        <div className='stationStatusListCell stationStatusListCell--time'>
                                            {formatUtcDateWithTime(report.dateTimeStarted)}
                                        </div>
                                        <div className='stationStatusListCell stationStatusListCell--status'>
                                            {report.unfinished
                                                ? t.gettext('Unfinished')
                                                : t.gettext('Finished')
                                            }
                                        </div>
                                        <div className='stationStatusListCell stationStatusListCell--count'>
                                            {report.totalItems}
                                        </div>
                                        <div className='stationStatusListCell stationStatusListCell--actions'>
                                            {!report.unfinished && (
                                                <button
                                                    type='button'
                                                    className='stationStatusActionButton'
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        openReport(report);
                                                    }}
                                                    title={t.gettext('View')}
                                                    aria-label={t.gettext('View')}
                                                >
                                                    <span className='material-icons'>visibility</span>
                                                </button>
                                            )}
                                            {!(index === 0 && report.unfinished) && (
                                                <button
                                                    type='button'
                                                    className='stationStatusActionButton stationStatusActionButton--delete'
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        setReportToDelete(report);
                                                    }}
                                                    title={t.gettext('Delete')}
                                                    aria-label={t.gettext('Delete')}
                                                >
                                                    <span className='material-icons'>delete</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))
                                : (
                                    <div className='stationStatusEmpty'>
                                        {t.gettext('No reports')}
                                    </div>
                                )
                            }
                        </div>
                    )
                }
            </div>

            {reportModalOpen && (
                <ReportDetailsModal
                    t={t}
                    report={selectedReport}
                    pending={detailsPending}
                    closeMethod={closeReport}
                    activityClass={activityClass}
                    statusClass={statusClass}
                    openIntervals={setIntervalsModal}
                />
            )}

            {stationModalOpen && (
                <StationSelectionModal
                    t={t}
                    pending={stationsPending}
                    stations={stationsForReport}
                    selectedStationIds={selectedStationIds}
                    setSelectedStationIds={setSelectedStationIds}
                    toggleStation={toggleStation}
                    closeMethod={closeStationSelection}
                    applyMethod={requestReport}
                />
            )}

            {intervalsModal && (
                <IntervalsModal
                    t={t}
                    sensorName={intervalsModal.sensorName}
                    intervals={intervalsModal.intervals}
                    closeMethod={() => setIntervalsModal(null)}
                />
            )}

            {reportToDelete && (
                <DeleteReportModal
                    t={t}
                    report={reportToDelete}
                    closeMethod={() => setReportToDelete(null)}
                    confirmMethod={confirmDelete}
                />
            )}
        </div>
    );
};

const ModalHeader = ({title, closeMethod}) => (
    <div className='stationStatusModalHeader'>
        <div className='stationStatusModalTitle'>{title}</div>
        <button
            type='button'
            className='stationStatusModalCloseButton'
            onClick={closeMethod}
        >
            <span className='material-icons'>close</span>
        </button>
    </div>
);

const ReportDetailsModal = ({
    t,
    report,
    pending,
    closeMethod,
    activityClass,
    statusClass,
    openIntervals
}) => (
    <div
        className='stationStatusModalBlur'
        onClick={e => {
            if (e.target === e.currentTarget) {
                closeMethod();
            }
        }}
    >
        <div className='stationStatusModal stationStatusModal--wide'>
            <ModalHeader title={t.gettext('Report details')} closeMethod={closeMethod}/>
            <div className='stationStatusModalContent'>
                {pending || !report
                    ? (
                        <div className='stationStatusModalSpinner'>
                            <Spinner size={180}/>
                        </div>
                    )
                    : (
                        <>
                            <div className='stationStatusReportMeta'>
                                <span>{t.gettext('Report time')}</span>
                                <strong>{formatUtcDateWithTime(report.dateTimeStarted)}</strong>
                            </div>
                            <div className='stationStatusReportItems'>
                                {report.items.map(item => (
                                    <div className='stationStatusReportItem' key={item.id}>
                                        <div className='stationStatusReportItemHeader'>
                                            <div>
                                                <span>{t.gettext('Station name')}</span>
                                                <div className='stationStatusStationTitle'>
                                                    <strong>{item.weatherStationName}</strong>
                                                    <StationInfoPopover
                                                        t={t}
                                                        item={item}
                                                    />
                                                </div>
                                                <span className='stationStatusStationOwner'>{item.userFullName || '-'}</span>
                                            </div>
                                            <div>
                                                <span>{t.gettext('Rain precipitation')}</span>
                                                <strong>{item.rainAmountMm || 0} {t.gettext('mm')}</strong>
                                            </div>
                                        </div>
                                        <div className='stationStatusSensorsTable'>
                                            <div className='stationStatusSensorsHeader'>
                                                <div>{t.gettext('Sensor Name')}</div>
                                                <div>{t.gettext('Sensor Type')}</div>
                                                <div>{t.gettext('Activity status')}</div>
                                                <div>{t.gettext('Stability status')}</div>
                                                <div/>
                                            </div>
                                            {item.sensors.map(sensor => (
                                                <div className='stationStatusSensorsRow' key={sensor.sensorId}>
                                                    <div>
                                                        <div className='stationStatusSensorTitle'>
                                                            <span className='stationStatusSensorName'>{sensor.sensorName || '-'}</span>
                                                            <SensorInfoPopover
                                                                t={t}
                                                                sensor={sensor}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div>{sensor.sensorType ? t.gettext(sensor.sensorType) : '-'}</div>
                                                    <div>
                                                        <span className={activityClass(sensor.active)}>
                                                            {sensor.active ? t.gettext('Active') : t.gettext('Not Active')}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <button
                                                            type='button'
                                                            className='stationStatusStabilityButton'
                                                            onClick={() => openIntervals({
                                                                sensorName: sensor.sensorName,
                                                                intervals: sensor.stability && sensor.stability.measurementIntervals
                                                                    ? sensor.stability.measurementIntervals
                                                                    : []
                                                            })}
                                                        >
                                                            <span className={statusClass(sensor.stability && sensor.stability.status)}>
                                                                {sensor.stability && sensor.stability.status
                                                                    ? t.gettext(sensor.stability.status)
                                                                    : '-'
                                                                }
                                                            </span>
                                                        </button>
                                                    </div>
                                                    <div>
                                                        {sensor.deleted && (
                                                            <span className='stationStatusBadge stationStatusBadge--red'>
                                                                {t.gettext('deleted')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )
                }
            </div>
        </div>
    </div>
);

const StationInfoPopover = ({t, item}) => {
    const infoRows = [
        {label: 'Station id', value: item.weatherStationId},
        {label: 'Station name', value: item.weatherStationName},
        {label: 'User id', value: item.userId, copy: true},
        {label: 'User name', value: item.userFullName},
        {label: 'User email', value: item.userEmail, copy: true}
    ];

    return (
        <div className='stationStatusInfoWrapper'>
            <button
                type='button'
                className='stationStatusInfoButton'
                aria-label={t.gettext('Station data')}
                title={t.gettext('Station data')}
            >
                <span className='material-icons'>info_outline</span>
            </button>
            <div className='stationStatusInfoPopover'>
                <div className='stationStatusInfoTitle'>{t.gettext('Station data')}</div>
                {infoRows.map(row => (
                    <div className='stationStatusInfoRow' key={row.label}>
                        <span>{t.gettext(row.label)}</span>
                        <strong>{row.value || '-'}</strong>
                        {row.copy && row.value && (
                            <CopyButton t={t} value={row.value}/>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const SensorInfoPopover = ({t, sensor}) => {
    const infoRows = [
        {label: 'Sensor id', value: sensor.sensorId, copy: true},
        {label: 'Sensor name', value: sensor.sensorName},
        {label: 'Sensor type', value: sensor.sensorType ? t.gettext(sensor.sensorType) : '-'},
        {label: 'Lora id', value: sensor.loraIdentifier, copy: true}
    ];

    return (
        <div className='stationStatusInfoWrapper'>
            <button
                type='button'
                className='stationStatusInfoButton'
                aria-label={t.gettext('Sensor data')}
                title={t.gettext('Sensor data')}
            >
                <span className='material-icons'>info_outline</span>
            </button>
            <div className='stationStatusInfoPopover stationStatusInfoPopover--sensor'>
                <div className='stationStatusInfoTitle'>{t.gettext('Sensor data')}</div>
                {infoRows.map(row => (
                    <div className='stationStatusInfoRow' key={row.label}>
                        <span>{t.gettext(row.label)}</span>
                        <strong>{row.value || '-'}</strong>
                        {row.copy && row.value && (
                            <CopyButton t={t} value={row.value}/>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const CopyButton = ({t, value}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = event => {
        event.preventDefault();
        event.stopPropagation();

        copyToBuffer(value).then(success => {
            if (success) {
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
            }
        });
    };

    return (
        <button
            type='button'
            className={copied
                ? 'stationStatusCopyButton stationStatusCopyButton--copied'
                : 'stationStatusCopyButton'
            }
            onClick={handleCopy}
            title={t.gettext('Copy to buffer')}
            aria-label={t.gettext('Copy to buffer')}
        >
            <span className='material-icons'>{copied ? 'check' : 'content_copy'}</span>
        </button>
    );
};

const StationSelectionModal = ({
    t,
    pending,
    stations,
    selectedStationIds,
    setSelectedStationIds,
    toggleStation,
    closeMethod,
    applyMethod
}) => (
    <div
        className='stationStatusModalBlur'
        onClick={e => {
            if (e.target === e.currentTarget) {
                closeMethod();
            }
        }}
    >
        <div className='stationStatusModal'>
            <ModalHeader title={t.gettext('Select stations')} closeMethod={closeMethod}/>
            <div className='stationStatusModalContent'>
                <div className='stationStatusSelectionActions'>
                    <button
                        type='button'
                        className='stationStatusSmallButton'
                        onClick={() => setSelectedStationIds(stations.map(station => station.id))}
                    >
                        {t.gettext('Select all')}
                    </button>
                    <button
                        type='button'
                        className='stationStatusSmallButton stationStatusSmallButton--secondary'
                        onClick={() => setSelectedStationIds([])}
                    >
                        {t.gettext('Deselect all')}
                    </button>
                </div>
                {pending
                    ? (
                        <div className='stationStatusModalSpinner'>
                            <Spinner size={180}/>
                        </div>
                    )
                    : (
                        <div className='stationStatusStationList'>
                            {stations.length
                                ? stations.map(station => (
                                    <label className='stationStatusStationOption' key={station.id}>
                                        <input
                                            type='checkbox'
                                            checked={selectedStationIds.includes(station.id)}
                                            onChange={() => toggleStation(station.id)}
                                        />
                                        <span className='stationStatusStationText'>
                                            <span className='stationStatusStationName'>{station.name}</span>
                                            <span className='stationStatusStationUser'>{station.userName}</span>
                                        </span>
                                    </label>
                                ))
                                : <div className='stationStatusEmpty'>{t.gettext('No stations')}</div>
                            }
                        </div>
                    )
                }
                <div className='stationStatusModalButtons'>
                    <button
                        type='button'
                        className='stationStatusCancelButton'
                        onClick={closeMethod}
                    >
                        {t.gettext('Cancel')}
                    </button>
                    <button
                        type='button'
                        className='stationStatusConfirmButton'
                        onClick={applyMethod}
                        disabled={!selectedStationIds.length}
                    >
                        {t.gettext('Apply')}
                    </button>
                </div>
            </div>
        </div>
    </div>
);

const IntervalsModal = ({t, sensorName, intervals, closeMethod}) => (
    <div
        className='stationStatusModalBlur stationStatusModalBlur--top'
        onClick={e => {
            if (e.target === e.currentTarget) {
                closeMethod();
            }
        }}
    >
        <div className='stationStatusModal stationStatusModal--small'>
            <ModalHeader title={t.gettext('Sensor stability intervals')} closeMethod={closeMethod}/>
            <div className='stationStatusModalContent'>
                <div className='stationStatusReportMeta'>
                    <span>{t.gettext('Sensor Name')}</span>
                    <strong>{sensorName || '-'}</strong>
                </div>
                <div className='stationStatusIntervalsTable'>
                    <div className='stationStatusIntervalsHeader'>
                        <div>{t.gettext('Measurement Time')}</div>
                        <div>{t.gettext('Interval')}</div>
                    </div>
                    {intervals.length
                        ? intervals.map((interval, index) => (
                            <div className='stationStatusIntervalsRow' key={index}>
                                <div>{formatDateWithTime(interval.measurementTime)}</div>
                                <div>{interval.interval}</div>
                            </div>
                        ))
                        : <div className='stationStatusEmpty'>{t.gettext('No intervals')}</div>
                    }
                </div>
            </div>
        </div>
    </div>
);

const DeleteReportModal = ({t, report, closeMethod, confirmMethod}) => (
    <div
        className='stationStatusModalBlur stationStatusModalBlur--top'
        onClick={e => {
            if (e.target === e.currentTarget) {
                closeMethod();
            }
        }}
    >
        <div className='stationStatusModal stationStatusModal--confirm'>
            <div className='stationStatusModalContent'>
                <div className='stationStatusDeleteText'>
                    {t.gettext('Delete report')} {formatUtcDateWithTime(report.dateTimeStarted)}?
                </div>
                <div className='stationStatusModalButtons'>
                    <button
                        type='button'
                        className='stationStatusCancelButton'
                        onClick={closeMethod}
                    >
                        {t.gettext('Cancel')}
                    </button>
                    <button
                        type='button'
                        className='stationStatusDeleteButton'
                        onClick={confirmMethod}
                    >
                        {t.gettext('Delete')}
                    </button>
                </div>
            </div>
        </div>
    </div>
);

export default StationStatus;
